import { makeStyles } from '@material-ui/core'
import { CloseButton, Elements, enums, ExpandingSection, Grid, hooks, models, PrintButton, ReportTable, TwoColRow, utils } from '@tfo/common'
import { DetailedArchivedReport } from '@tfo/common/src/interfaces'
import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'
import bkflogo from '../../../../media/images/common/BKF-standard-b.png'
import logo from '../../../../media/svg/tfo-logo.svg'
import $http from '../../../../utils/$http'

const useStyles = makeStyles({
    printButton: {
        marginTop: '4rem',
        float: 'right',
        // Don't display the print button when printing .
        '@media print': {
            display: 'none'
        }
    },
    hideOnPrint: {
        '@media print': {
            display: 'none'
        }
    },
    readArchiveReport: {
        float: 'none',
    },
    checkpoints: {
        // Adjust the position of the grid, so the checkpoint titles (column 2) is aligned with the section header.
        // This needs to correlate with gridTemplateColumn width plus the columnGap.
        marginLeft: '-5rem',
        display: 'grid',
        //two columns: discrepancy indicator and checkpoint label
        gridTemplateColumns: '4rem auto',
        gridTemplateRows: '4rem',
        columnGap: '1rem',
        rowGap: '0.5rem',
        alignItems: 'center'
    },
    checkpointLabel: {
        gridColumnStart: 2 //checkpoint label should always be displayed in column 2.
    },
    // Avoid breaking the container when printing.
    noPageBreak: {
        'page-break-inside': 'avoid'
    },
    "recruiment-process": {
        "& p": {
            "margin-bottom": "2rem"
        }
    },
    errorMessage: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center'
    },
})

const { Label, Paragraph, Heading4 } = Elements
const { GridWrapper } = Grid

const ArchivedReport = () => {
    const { orderId } = useParams<{ orderId: string }>()
    const store = hooks.useStore()
    const categories: models.CheckpointCategory[] = [models.CheckpointCategory.Personalia, models.CheckpointCategory.Financial, models.CheckpointCategory.Legal, models.CheckpointCategory.Resume, models.CheckpointCategory.Media]
    const manuallyDepersonalizedReasons: enums.ReasonForDepersonalization[] = [enums.ReasonForDepersonalization.Canceled, enums.ReasonForDepersonalization.DuplicateOrder, enums.ReasonForDepersonalization.IncorrectOrder, enums.ReasonForDepersonalization.WithdrawnOrder]
    const { clientViewStore, userStore } = store
    const isOrderer = utils.isOrderer(userStore.user)
    const [archivedReport, setArchivedReport] = useState<DetailedArchivedReport>({} as DetailedArchivedReport)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [hasError, setHasError] = useState<boolean>(false)
    const styles = useStyles()
    const intl = useIntl()
    const history = useHistory()

    useEffect(() => {
        if (orderId) {
            getArchivedReportData()
        }
    }, [orderId])

    const printArchivedReport = (): void => {
        window.print()
    }

    const getArchivedReportData = async () => {
        setHasError(() => false)
        setIsLoading(() => true)

        try {
            const archivedReportResponse = await $http.get<DetailedArchivedReport>(`/ReportArchive/${orderId}`)

            setArchivedReport(archivedReportResponse)
        } catch (error) {
            setIsLoading(() => false)
            setHasError(() => true)
            console.error(error)
        }

        setIsLoading(() => false)
    }

    const isNotEmptyArray = (array: Array<string>): boolean => {

        if (!Array.isArray(array) || !array.length) {
            return false
        }

        return true
    }

    return (
        <>
            <div
                className={classnames(
                    'ClientContainerOverlay',
                    { 'ClientContainerOverlay--initialLoad': isLoading },
                    { 'ClientContainerOverlay--loading': isLoading },
                    {
                        'ClientContainerOverlay--animateIn': isLoading
                    }
                )}
                style={{ position: 'absolute', left: 0 }}>

                <div className={'ReadReport'}>
                    {
                        hasError ? (
                            <div className={styles.errorMessage}>
                                <CloseButton
                                    className={'ReadReport__Close'}
                                    onClick={(() => history.goBack())}
                                />
                                <Paragraph>{intl.formatMessage({ id: 'archived.report.errormessage' })}</Paragraph>
                            </div>
                        ) : <>
                            <div className={classnames('ReadReportHeader', 'ReadReportHeader--narrow')}>
                                <img src={logo} className="ReadReport__Logo" />
                                <GridWrapper>
                                    <div className={classnames(['ReadReportHeader__Heading', 'printWidth'].join(' '))}>
                                        <Paragraph className={'ReadReportHeader__Paragraph'}>
                                            {intl.formatMessage({ id: 'archived.report.disclaimer' })}
                                        </Paragraph>
                                    </div>
                                </GridWrapper>

                                <CloseButton
                                    className={'ReadReport__Close'}
                                    onClick={(() => history.goBack())}
                                />
                            </div>
                            <GridWrapper>
                                <div className={'ReadReportContent'}>
                                    {<PrintButton
                                        text={intl.formatMessage({ id: 'common.print' })}
                                        className={styles.printButton}
                                        onClick={() => printArchivedReport()} />}
                                    <div>
                                        <>
                                            {
                                                (!manuallyDepersonalizedReasons.includes(archivedReport?.depersonalizationReason)) &&
                                                (!isOrderer || archivedReport.reportSummary !== "") &&
                                                < div className={classnames(styles.hideOnPrint)} style={{ paddingTop: 70 }} id={'Specifications'}>
                                                    <Heading4>{intl.formatMessage({ id: 'readreport.label.reportSummary' })}</Heading4>
                                                    <Paragraph>
                                                        {archivedReport.reportSummary === "" ? intl.formatMessage({ id: 'defaultValue.reportSummary' }) : archivedReport.reportSummary}
                                                    </Paragraph>
                                                </div>
                                            }
                                            <div className={'ReadReportContent__Section'}>
                                                <Heading4>{intl.formatMessage({ id: 'readreport.label.orderspecifications' })}</Heading4>
                                                <div
                                                    className={
                                                        'ScrollableContainer ReadReportContent__Space--medium'
                                                    }>
                                                    <ReportTable className="ReadReport__Table TwoColTable">

                                                        <div>
                                                            {(clientViewStore.company.tfoAcceptsOrDeniesCandidate && archivedReport.candidateDeterminationIsAccepted !== null) &&
                                                                <TwoColRow
                                                                    label={intl.formatMessage({ id: 'readreport.label.tfodecision' }, { company: archivedReport.companyName, user: archivedReport.creatorName })}
                                                                    className={styles['recruiment-process']}
                                                                    smallerWidth={true}
                                                                    value={
                                                                        (archivedReport.candidateDeterminationIsAccepted)
                                                                            ? intl.formatMessage({ id: 'readreport.tfodecision.accept' })
                                                                            : intl.formatMessage({ id: 'readreport.tfodecision.deny' })
                                                                    }
                                                                />
                                                            }
                                                            <TwoColRow
                                                                label={intl.formatMessage({ id: 'readreport.label.individualID' })}
                                                                value={archivedReport.controlPerson}
                                                                lineHeightSmall={true}
                                                                smallerWidth={true}
                                                            />
                                                            {archivedReport.individualReference && (
                                                                <TwoColRow
                                                                    label={intl.formatMessage({ id: 'readreport.label.individualreference' })}
                                                                    value={archivedReport.individualReference}
                                                                    lineHeightSmall={true}
                                                                    smallerWidth={true}
                                                                />
                                                            )}


                                                            <TwoColRow
                                                                label={intl.formatMessage({ id: 'readreport.label.ordering.date' })}
                                                                value={`${intl.formatDate(archivedReport.created)}`}
                                                                lineHeightSmall={true}
                                                                smallerWidth={true}
                                                            />


                                                            <TwoColRow
                                                                label={`${intl.formatMessage({ id: 'archived.report.lastApproval' })}`}
                                                                value={archivedReport.lastApproval ? `${intl.formatDate(archivedReport.lastApproval)}` : ' - '}
                                                                lineHeightSmall={true}
                                                                smallerWidth={true}
                                                            />

                                                            {
                                                                (archivedReport.delivered && archivedReport.depersonalizationReason !== null && archivedReport.depersonalizationReason !== undefined) ?
                                                                    <TwoColRow
                                                                        lineHeightSmall={true}
                                                                        smallerWidth={true}
                                                                        label={intl.formatMessage({ id: 'readreport.label.delivered' })}
                                                                        value={`${intl.formatDate(archivedReport.delivered)}`}
                                                                    /> : <>
                                                                        {archivedReport.depersonalizationTimestamp &&

                                                                            <TwoColRow
                                                                                lineHeightSmall={true}
                                                                                smallerWidth={true}
                                                                                label={intl.formatMessage({ id: 'readreport.label.canceled' })}
                                                                                value={`${intl.formatDate(archivedReport.depersonalizationTimestamp)}`}
                                                                            />
                                                                        }
                                                                        {archivedReport.depersonalizationReason &&
                                                                            <TwoColRow label={intl.formatMessage({ id: 'readreport.label.reason' })}
                                                                                lineHeightSmall={true}
                                                                                smallerWidth={true}
                                                                                value={`${intl.formatMessage({ id: `depersonalizeReason.${archivedReport.depersonalizationReason}` })}`}
                                                                            />
                                                                        }

                                                                    </>
                                                            }
                                                        </div>
                                                        <div style={{ marginTop: 20 }}>
                                                            <TwoColRow
                                                                lineHeightSmall={true}
                                                                smallerWidth={true}
                                                                label={intl.formatMessage({ id: 'readreport.label.customer' })}
                                                                value={archivedReport.companyName}
                                                            />
                                                            <TwoColRow
                                                                lineHeightSmall={true}
                                                                smallerWidth={true}
                                                                label={intl.formatMessage({ id: 'readreport.label.client' })}
                                                                value={archivedReport.creatorName}
                                                            />
                                                            <TwoColRow
                                                                lineHeightSmall={true}
                                                                smallerWidth={true}
                                                                label={intl.formatMessage({ id: 'readreport.label.reportpackage' })}
                                                                value={archivedReport.reportPackageName}
                                                            />
                                                            {isNotEmptyArray(archivedReport.targetCountries) &&
                                                                <TwoColRow
                                                                    lineHeightSmall={true}
                                                                    smallerWidth={true}
                                                                    label={intl.formatMessage({ id: 'readreport.label.controlConductedIn' })}
                                                                    value={archivedReport.targetCountries.join(', ')}
                                                                />
                                                            }
                                                            {isNotEmptyArray(archivedReport.partnerName) &&
                                                                <TwoColRow
                                                                    lineHeightSmall={true}
                                                                    smallerWidth={true}
                                                                    label={intl.formatMessage({ id: 'readreport.label.partnerAddons' })}
                                                                    value={archivedReport.partnerName.join(', ')}
                                                                />}
                                                        </div>
                                                    </ReportTable>
                                                </div>
                                            </div>
                                        </>
                                    </div>
                                    <div>
                                        {(
                                            archivedReport &&
                                            Array.isArray(archivedReport.checkpointInfo) &&
                                            archivedReport.checkpointInfo.some(setting => {
                                                return archivedReport?.checkpointSet?.includes(setting.checkpointType)
                                            })
                                        ) &&
                                            <ExpandingSection
                                                className={'ScrollableContainer ReadReportContent__Space--medium'}
                                                caption={intl.formatMessage({ id: 'readreport.label.controlpoints' })}>

                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'auto auto auto auto',
                                                    columnGap: '10px',
                                                    maxWidth: 'max-content'
                                                }}>
                                                    {
                                                        categories.map(category => (
                                                            <div key={category}>
                                                                {
                                                                    archivedReport.checkpointInfo.some(setting => archivedReport?.checkpointSet.includes(setting.checkpointType) && setting.category === category) &&
                                                                    <>
                                                                        <Label>{intl.formatMessage({ id: `archived.report.category.label.${category}` })}</Label>
                                                                        <ul style={{ marginLeft: '-25px' }}>
                                                                            {
                                                                                archivedReport.checkpointInfo.filter(setting => setting.category === category).map(checkpoint => (
                                                                                    <>
                                                                                        {
                                                                                            (archivedReport?.checkpointSet.includes(checkpoint.checkpointType)) &&
                                                                                            (
                                                                                                <li>{intl.formatMessage({ id: `service.checkpoints.${checkpoint.checkpointType}` })}</li>
                                                                                            )
                                                                                        }
                                                                                    </>
                                                                                ))
                                                                            }
                                                                        </ul>
                                                                    </>
                                                                }
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </ExpandingSection>
                                        }
                                        {(
                                            archivedReport &&
                                            archivedReport.delivered
                                        ) &&
                                            <ExpandingSection
                                                className={'ScrollableContainer ReadReportContent__Space--medium'}
                                                caption={intl.formatMessage({ id: 'readreport.label.deliveryInformation' })}>
                                                {<ReportTable className="ReadReport__Table TwoColTable">
                                                    <div>
                                                        <TwoColRow
                                                            lineHeightSmall={true}
                                                            smallerWidth={true}
                                                            label={intl.formatMessage({ id: 'readreport.label.delivered' })}
                                                            value={intl.formatDate(archivedReport.delivered)}
                                                        />
                                                        <TwoColRow
                                                            lineHeightSmall={true}
                                                            smallerWidth={true}
                                                            label={intl.formatMessage({ id: 'readreport.label.readBy' })}
                                                            value={archivedReport.readReceipts.map(x => `${intl.formatDate(x.time)} - ${x.userName}`).join('\n')}
                                                        />
                                                        {archivedReport.reportRecipients?.map((recipient, index) => (
                                                            <TwoColRow
                                                                key={recipient.userId}
                                                                lineHeightSmall={true}
                                                                smallerWidth={true}
                                                                label={!index && intl.formatMessage({ id: 'archived.report.label.report-recipients' })}
                                                                value={recipient.fullName}
                                                            />
                                                        ))}
                                                    </div>
                                                </ReportTable>}
                                            </ExpandingSection>
                                        }
                                        <div>
                                            <img src={bkflogo} className={'ReadReport__BKFlogo'} style={{ float: 'right', width: '200px', paddingTop: 80, marginRight: '-40px' }} />
                                        </div>
                                    </div>
                                </div>
                            </GridWrapper>
                        </>
                    }
                </div>
            </div>
        </>
    )
}
export default ArchivedReport